import { resolveDataSourceUrl } from '@de/config/allowed-data-sources';
import type { ApiConfig } from '@de/types/config';
import {
  mapOfferingDetailToResultDetailFields,
  resolveOfferingDetailOrg,
  type ResultDetailFields,
} from '@de/utils/mappers/mapOfferingDetailToResultDetailFields';
import { getApiConfig } from '../../services/api/utils';

export type FetchOfferingDetailFieldsOptions = {
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
  apiConfig?: ApiConfig;
};

/**
 * Fetch `GET /v1/offerings/:id` and map the org detail projection onto the
 * drawer-detail subset of `Result` (TCPA, value props, …). Used by comparison
 * hydration so compare columns can show school TCPA without going through the
 * single-slot lead-drawer DSM `offeringDetail` source.
 *
 * Never throws.
 * - `null` — transport/policy failure (caller may retry)
 * - `{}` — response OK but no mappable detail fields
 * - fields — mapped drawer-detail patch
 */
export async function fetchOfferingDetailFields(
  offeringId: string,
  options: FetchOfferingDetailFieldsOptions = {},
): Promise<ResultDetailFields | null> {
  const trimmedId = offeringId.trim();
  if (!trimmedId) {
    return null;
  }

  const apiConfig = options.apiConfig ?? getApiConfig();
  let url: string;
  try {
    url = resolveDataSourceUrl(
      'offeringsService',
      `/v1/offerings/${encodeURIComponent(trimmedId)}`,
      'GET',
      apiConfig,
    );
  } catch {
    return null;
  }

  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  try {
    const response = await fetchImpl(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: options.signal,
    });
    if (!response.ok) {
      return null;
    }
    const json: unknown = await response.json();
    const orgDetail = resolveOfferingDetailOrg(json);
    return mapOfferingDetailToResultDetailFields(orgDetail);
  } catch {
    return null;
  }
}
